<script setup lang="ts">
import { ApiClientError } from '@shopware/api-client'
import type { ProductWishlistToggleProps } from '#nuxtware/types/product-wishlist-toggle'

const props = withDefaults(defineProps<ProductWishlistToggleProps>(), {
    variant: 'icon',
    size: 'medium',
    disabled: false
})

const { t } = useI18n()
const { success, error } = useGlobalNotifications()

// Hover state
const isHovered = ref(false)

// Wishlist functionality
const { addToWishlist, removeFromWishlist, isInWishlist } = useProductWishlist(props.product.id)

// Computed properties for styling and accessibility
const buttonSize = computed(() => {
    if (props.variant === 'button') {
        return props.size
    }
    // For icon variant, ensure minimum 44px touch target for accessibility
    return props.size === 'small' ? 'medium' : props.size
})

const iconSize = computed(() => {
    switch (props.size) {
    case 'small': return 'sm'
    case 'large': return 'lg'
    default: return 'md'
    }
})

const ariaLabel = computed(() => {
    return isInWishlist.value 
        ? t('wishlist.removeFromWishlist')
        : t('wishlist.addToWishlist')
})

const buttonText = computed(() => {
    return isInWishlist.value 
        ? t('wishlist.removeFromWishlist')
        : t('wishlist.addToWishlist')
})

const productName = computed(() => props.product.translated?.name || '')

// Icon name - show filled heart when in wishlist OR when hovering
const iconName = computed(() => {
    if (isInWishlist.value) return 'heart-filled'
    return isHovered.value ? 'heart-filled' : 'heart'
})

// Wishlist toggle functionality
const toggleWishlistProduct = async () => {
    if (props.disabled) return

    try {
        if (isInWishlist.value) {
            await removeFromWishlist()
            success(t('wishlist.messages.removedFromWishlist', { product: productName.value }))
        } else {
            await addToWishlist()
            success(t('wishlist.messages.addedToWishlist', { product: productName.value }))
        }
    } catch (err) {
        // Handle specific API errors if available
        if (err instanceof ApiClientError && err.details?.errors?.[0]?.detail) {
            error(`${t('wishlist.messages.wishlistError')} ${err.details.errors[0].detail}`)
        } else {
            error(t('wishlist.messages.wishlistError'))
        }
        console.error('Wishlist toggle error:', err)
    }
}
</script>

<template>
    <FoundationButton
        variant="ghost"
        :size="buttonSize"
        :disabled="disabled"
        :aria-label="ariaLabel"
        circle
        class="product-wishlist-toggle focus-style group"
        data-testid="product-wishlist-button"
        @click.stop="toggleWishlistProduct"
        @mouseenter="isHovered = true"
        @mouseleave="isHovered = false"
        @touchstart="isHovered = true"
        @touchend="isHovered = false"
        @touchcancel="isHovered = false"
    >
        <ClientOnly>
            <template v-if="variant === 'icon'">
                <FoundationIcon
                    :name="iconName"
                    :size="iconSize"
                    :class="[
                        'transition-colors duration-200 group-hover:animate-none group-focus:animate-none',
                        isInWishlist ? 'text-wishlist-toggle hover:stroke-primary' : 'text-wishlist-toggle hover:text-wishlist-toggle'
                    ]"
                    :aria-hidden="true"
                />
            </template>
            <template v-else>
                <FoundationIcon
                    :name="iconName"
                    :size="iconSize"
                    :class="[
                        'mr-2 transition-colors duration-200',
                        isInWishlist ? 'text-wishlist-toggle-active' : 'text-wishlist-toggle'
                    ]"
                    :aria-hidden="true"
                />
                {{ buttonText }}
            </template>
            <template #fallback>
                <FoundationIcon
                    name="heart"
                    :size="iconSize"
                    :class="variant === 'icon' ? 'text-wishlist-toggle' : 'mr-2 text-wishlist-toggle'"
                    :aria-hidden="true"
                />
                <template v-if="variant !== 'icon'">{{ $t('wishlist.addToWishlist') }}</template>
            </template>
        </ClientOnly>
    </FoundationButton>
</template>
